// Data for the "What would you like to drive next?" post-publish flow.
// Brands and models are real, well-known vehicles available in Canada —
// curated for a preference-discovery UI, not represented as live inventory.

export const CATEGORIES = [
  { key: 'suv', label: 'SUV', icon: '🚙' },
  { key: 'car', label: 'Car', icon: '🚗' },
  { key: 'truck', label: 'Truck', icon: '🛻' },
  { key: 'electric', label: 'Electric', icon: '⚡' },
  { key: 'sports', label: 'Sports Car', icon: '🏎' },
  { key: 'van', label: 'Van', icon: '🚐' },
  { key: 'camper', label: 'Camper', icon: '🏕' },
  { key: 'trailer', label: 'Trailer', icon: '🚛' },
  { key: 'equipment', label: 'Equipment', icon: '🚜' },
  { key: 'motorcycle', label: 'Motorcycle', icon: '🏍' },
  { key: 'aircraft', label: 'Aircraft', icon: '✈' },
  { key: 'unsure', label: "I'm not sure yet", icon: '❓' },
];

// Brands offered per category (real, Canada-relevant). "No Preference" is
// appended automatically wherever this list is rendered — kept out of the
// data itself so it never accidentally gets treated as a real brand.
export const CATEGORY_BRANDS = {
  suv: [
    'Toyota',
    'Honda',
    'Ford',
    'Chevrolet',
    'Jeep',
    'Hyundai',
    'Kia',
    'Mazda',
    'Subaru',
    'Nissan',
  ],
  car: ['Honda', 'Toyota', 'Mazda', 'Hyundai', 'Kia', 'Volkswagen', 'Nissan', 'Subaru'],
  truck: ['Ford', 'Ram', 'Chevrolet', 'GMC', 'Toyota', 'Nissan', 'Jeep'],
  electric: ['Tesla', 'Ford', 'Chevrolet', 'Hyundai', 'Kia', 'Volkswagen', 'Nissan'],
  sports: ['Ford', 'Chevrolet', 'Porsche', 'BMW', 'Nissan', 'Toyota', 'Mazda'],
  van: ['Honda', 'Toyota', 'Chrysler', 'Kia', 'Ford', 'Ram', 'Mercedes-Benz'],
  camper: ['Forest River', 'Jayco', 'Keystone', 'Winnebago', 'Thor'],
  trailer: ['Featherlite', 'PJ Trailers', 'Big Tex', 'Mirage', 'Wells Cargo'],
  equipment: ['John Deere', 'Caterpillar', 'Kubota', 'Case IH', 'New Holland', 'Bobcat'],
  motorcycle: ['Harley-Davidson', 'Honda', 'Yamaha', 'Kawasaki', 'Suzuki', 'BMW', 'Ducati'],
  aircraft: ['Cessna', 'Piper', 'Cirrus', 'Diamond', 'Beechcraft'],
};

// Models offered per category + brand, for the categories where a model
// step is meaningful. Categories not listed here (camper, trailer,
// equipment, aircraft) skip straight from brand to priorities.
export const CATEGORY_BRAND_MODELS = {
  truck: {
    Ford: ['F-150', 'Ranger', 'Super Duty', 'Maverick'],
    Ram: ['1500', '2500', '3500', 'ProMaster'],
    Chevrolet: ['Silverado 1500', 'Silverado HD', 'Colorado'],
    GMC: ['Sierra 1500', 'Sierra HD', 'Canyon'],
    Toyota: ['Tacoma', 'Tundra'],
    Nissan: ['Frontier', 'Titan'],
    Jeep: ['Gladiator'],
  },
  suv: {
    Toyota: ['RAV4', 'Highlander', '4Runner', 'Land Cruiser'],
    Honda: ['CR-V', 'HR-V', 'Pilot'],
    Ford: ['Escape', 'Edge', 'Explorer', 'Bronco'],
    Chevrolet: ['Equinox', 'Trailblazer', 'Traverse', 'Tahoe'],
    Jeep: ['Compass', 'Cherokee', 'Grand Cherokee', 'Wrangler'],
    Hyundai: ['Tucson', 'Santa Fe', 'Palisade'],
    Kia: ['Sportage', 'Sorento', 'Telluride'],
    Mazda: ['CX-5', 'CX-30', 'CX-90'],
    Subaru: ['Forester', 'Outback', 'Crosstrek'],
    Nissan: ['Rogue', 'Murano', 'Pathfinder'],
  },
  car: {
    Honda: ['Civic', 'Accord'],
    Toyota: ['Corolla', 'Camry', 'Prius'],
    Mazda: ['Mazda3', 'Mazda6'],
    Hyundai: ['Elantra', 'Sonata'],
    Kia: ['Forte', 'K5'],
    Volkswagen: ['Jetta', 'Golf'],
    Nissan: ['Sentra', 'Altima'],
    Subaru: ['Impreza', 'Legacy'],
  },
  electric: {
    Tesla: ['Model 3', 'Model Y', 'Model S', 'Model X'],
    Ford: ['Mustang Mach-E', 'F-150 Lightning'],
    Chevrolet: ['Bolt EV', 'Bolt EUV', 'Equinox EV'],
    Hyundai: ['Ioniq 5', 'Ioniq 6', 'Kona Electric'],
    Kia: ['EV6', 'Niro EV'],
    Volkswagen: ['ID.4'],
    Nissan: ['Leaf', 'Ariya'],
  },
  sports: {
    Ford: ['Mustang'],
    Chevrolet: ['Corvette', 'Camaro'],
    Porsche: ['911', 'Cayman', 'Boxster'],
    BMW: ['M2', 'M4', 'Z4'],
    Nissan: ['Z'],
    Toyota: ['GR86', 'Supra'],
    Mazda: ['MX-5 Miata'],
  },
  van: {
    Honda: ['Odyssey'],
    Toyota: ['Sienna'],
    Chrysler: ['Pacifica', 'Grand Caravan'],
    Kia: ['Carnival'],
    Ford: ['Transit', 'Transit Connect'],
    Ram: ['ProMaster', 'ProMaster City'],
    'Mercedes-Benz': ['Sprinter', 'Metris'],
  },
  motorcycle: {
    'Harley-Davidson': ['Sportster', 'Softail', 'Road King'],
    Honda: ['CBR', 'Rebel', 'Africa Twin'],
    Yamaha: ['MT-07', 'YZF-R6', 'Ténéré 700'],
    Kawasaki: ['Ninja', 'Z650', 'Versys'],
    Suzuki: ['GSX-R', 'V-Strom'],
    BMW: ['R 1250 GS', 'S 1000 RR'],
    Ducati: ['Panigale', 'Monster'],
  },
};

export const PRIORITIES = [
  { key: 'payment', label: 'Lowest payment' },
  { key: 'price', label: 'Lowest price' },
  { key: 'fuelEconomy', label: 'Fuel economy' },
  { key: 'towing', label: 'Towing' },
  { key: 'family', label: 'Family' },
  { key: 'luxury', label: 'Luxury' },
  { key: 'offroad', label: 'Off-road' },
  { key: 'business', label: 'Business use' },
];
